// -*- coding: utf-8 -*-
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Skeleton from '@mui/material/Skeleton';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Divider from '@mui/material/Divider';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Chip from '@mui/material/Chip';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import { getApiBase, fetchJson } from '../../apiBase';

// API helpers
function api() {
  const base = `${getApiBase()}/admin`;
  return {
    classes: (schoolId) => `${base}/classes/${encodeURIComponent(schoolId)}`,
    studentsByClass: (schoolId, classId) => `${base}/students/${encodeURIComponent(schoolId)}/by-class/${encodeURIComponent(classId)}`,
    lessons: (schoolId, classId) => `${base}/school-exam-results/${encodeURIComponent(schoolId)}/lessons?class_id=${encodeURIComponent(classId)}`,
    list: (schoolId, params) => {
      const usp = new URLSearchParams();
      if (params.class_id) usp.set('class_id', params.class_id);
      if (params.lesson_id) usp.set('lesson_id', params.lesson_id);
      return `${base}/school-exam-results/${encodeURIComponent(schoolId)}?${usp.toString()}`;
    },
    upsert: (schoolId) => `${base}/school-exam-results/${encodeURIComponent(schoolId)}`,
    delete: (schoolId) => `${base}/school-exam-results/${encodeURIComponent(schoolId)}`,
  };
}

// Build wide row structure for UI table
// Build table rows combining existing results and synthetic empty rows so that
// for each student all lessons are visible (even if no score yet) allowing entry.
function buildWideRows(students, rawResults, lessonFilter, lessonsList) {
  const studentMap = new Map();
  students.forEach(s => {
    studentMap.set(s.id, {
      student_id: s.id,
      full_name: `${s.name || ''} ${s.surname || ''}`.trim(),
      class_name: s.class_name,
      // Per lesson grouping later
    });
  });
  // Group results per student->lesson
  const byStudentLesson = {};
  rawResults.filter(r => !lessonFilter || r.lesson_id === lessonFilter)
    .forEach(r => {
      const key = `${r.student_id}::${r.lesson_id}`;
      if (!byStudentLesson[key]) {
        byStudentLesson[key] = {
          student_id: r.student_id,
          lesson_id: r.lesson_id,
          lesson_code: r.lesson_code,
          lesson_name: r.lesson_name,
          t1: {1: null, 2: null, 3: null},
          t2: {1: null, 2: null, 3: null},
        };
      }
      const bucket = r.term === 1 ? byStudentLesson[key].t1 : byStudentLesson[key].t2;
      bucket[r.exam_number] = typeof r.score === 'number' ? r.score : (r.score == null ? null : Number(r.score));
    });
  // Compute rows (each lesson separate row per student)
  const rows = [];
  Object.values(byStudentLesson).forEach(entry => {
    const sInfo = studentMap.get(entry.student_id);
    if (!sInfo) return;
    const avg = (vals) => {
      const arr = [vals[1], vals[2], vals[3]].filter(v => v != null && !Number.isNaN(v));
      if (!arr.length) return null;
      return arr.reduce((a,b)=>a+b,0) / arr.length;
    };
    rows.push({
      student_id: entry.student_id,
      full_name: sInfo.full_name,
      class_name: sInfo.class_name,
      lesson_id: entry.lesson_id,
      lesson_code: entry.lesson_code,
      lesson_name: entry.lesson_name,
      term1: { ...entry.t1, avg: avg(entry.t1) },
      term2: { ...entry.t2, avg: avg(entry.t2) },
    });
  });
  // Add synthetic empty rows:
  //  - If a specific lessonFilter is chosen: each student gets at least one row for that lesson.
  //  - If NO lessonFilter: each student gets rows for ALL lessons in lessonsList.
  const ensureEmptyRow = (student, lessonId, lessonCode, lessonName) => {
    const exists = rows.some(r => r.student_id === student.id && r.lesson_id === lessonId);
    if (exists) return;
    rows.push({
      student_id: student.id,
      full_name: `${student.name || ''} ${student.surname || ''}`.trim(),
      class_name: student.class_name,
      lesson_id: lessonId,
      lesson_code: lessonCode,
      lesson_name: lessonName,
      term1: {1:null,2:null,3:null,avg:null},
      term2: {1:null,2:null,3:null,avg:null},
    });
  };
  if (lessonFilter) {
    const lessonObj = Array.isArray(lessonsList) ? lessonsList.find(l => l.lesson_id === lessonFilter) : null;
    const lessonCode = lessonObj ? lessonObj.lesson_code : String(lessonFilter);
    const lessonName = lessonObj ? lessonObj.lesson_name : lessonCode;
    students.forEach(stu => ensureEmptyRow(stu, lessonFilter, lessonCode, lessonName));
  } else if (Array.isArray(lessonsList) && lessonsList.length) {
    students.forEach(stu => {
      lessonsList.forEach(lesson => ensureEmptyRow(stu, lesson.lesson_id, lesson.lesson_code, lesson.lesson_name));
    });
  }
  // Sort by name
  rows.sort((a,b)=> a.full_name.localeCompare(b.full_name,'tr-TR'));
  return rows;
}

export default function SchoolExamResultsPage({ schoolId, onError, user }) {
  const apis = useMemo(() => api(), []);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dividerColor = theme.palette.divider;
  const termColors = useMemo(() => {
    const isDark = theme.palette.mode === 'dark';
    return {
      t1Cell: isDark ? 'rgba(25,118,210,0.18)' : '#F5F9FF', // subtle blue tint
      t2Cell: isDark ? 'rgba(46,125,50,0.22)' : '#F4FBF4', // subtle green tint
      t1Header: isDark ? 'rgba(25,118,210,0.28)' : '#E3F2FD',
      t2Header: isDark ? 'rgba(46,125,50,0.30)' : '#E8F5E9',
      divider: dividerColor,
    };
  }, [theme.palette.mode, dividerColor]);
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedStudentId, setSelectedStudentId] = useState('');

  const [loadingStudents, setLoadingStudents] = useState(false);
  const [students, setStudents] = useState([]);

  const [loadingLessons, setLoadingLessons] = useState(false);
  const [lessons, setLessons] = useState([]);
  const [selectedLesson, setSelectedLesson] = useState(null);
  // Deduplicated lesson options (by lesson_id) and enriched with label for Autocomplete value consistency
  const lessonOptions = useMemo(() => {
    const map = new Map();
    lessons.forEach(l => {
      if (!map.has(l.lesson_id)) map.set(l.lesson_id, l);
    });
    return Array.from(map.values()).map(l => ({ ...l, label: l.lesson_name }));
  }, [lessons]);

  const [loadingResults, setLoadingResults] = useState(false);
  const [rawResults, setRawResults] = useState([]);

  const [editingCell, setEditingCell] = useState(null); // {student_id, lesson_id, term, exam_number}
  const [pendingValue, setPendingValue] = useState('');
  const [saving, setSaving] = useState(false);

  // Load classes initially
  useEffect(() => {
    let active = true;
    (async () => {
      if (!schoolId) return;
      setLoadingClasses(true);
      try {
        const res = await fetchJson(apis.classes(schoolId));
        if (res.ok) {
          const data = await res.json();
          const classList = Array.isArray(data) ? data.filter(c => c && c.id) : [];
          classList.sort((a,b)=> String(a.name || '').localeCompare(String(b.name || ''),'tr-TR'));
          if (active) setClasses(classList);
        }
      } catch (_) { /* ignore */ }
      finally { if (active) setLoadingClasses(false); }
    })();
    return () => { active = false; };
  }, [schoolId, apis]);

  // Load students when class selected
  useEffect(() => {
    let active = true;
    if (!selectedClass) { setStudents([]); return; }
    (async () => {
      setLoadingStudents(true);
      try {
        const res = await fetchJson(apis.studentsByClass(schoolId, selectedClass.id));
        if (res.ok) {
          const data = await res.json();
          if (active) setStudents(Array.isArray(data) ? data : []);
        }
      } catch (e) { onError && onError('Öğrenciler yüklenemedi'); }
      finally { if (active) setLoadingStudents(false); }
    })();
    return () => { active = false; };
  }, [schoolId, selectedClass, apis, onError]);

  // Load lessons for class when class selected
  useEffect(() => {
    let active = true;
    if (!selectedClass) { setLessons([]); return; }
    (async () => {
      setLoadingLessons(true);
      try {
        const res = await fetchJson(apis.lessons(schoolId, selectedClass.id));
        if (res.ok) {
          const data = await res.json();
          const items = Array.isArray(data?.items) ? data.items : [];
          if (active) setLessons(items);
        }
      } catch (_) { /* ignore */ }
      finally { if (active) setLoadingLessons(false); }
    })();
    return () => { active = false; };
  }, [schoolId, selectedClass, apis]);

  const loadResults = useCallback(async () => {
    if (!selectedClass) return;
    setLoadingResults(true);
    try {
      const res = await fetchJson(apis.list(schoolId, { class_id: selectedClass.id, lesson_id: selectedLesson?.lesson_id || undefined }));
      if (!res.ok) throw new Error('fail');
      const data = await res.json();
      setRawResults(Array.isArray(data?.items) ? data.items : []);
    } catch(e) {
      onError && onError('Sonuçlar yüklenemedi');
      setRawResults([]);
    } finally { setLoadingResults(false); }
  }, [selectedClass, selectedLesson, schoolId, apis, onError]);

  useEffect(() => { loadResults(); }, [loadResults]);

  const wideRows = useMemo(() => buildWideRows(students, rawResults, selectedLesson?.lesson_id || null, lessonOptions), [students, rawResults, selectedLesson, lessonOptions]);
  const visibleRows = useMemo(() => {
    if (!selectedStudentId) return wideRows;
    return wideRows.filter(r => r.student_id === selectedStudentId);
  }, [wideRows, selectedStudentId]);

  const startEdit = (row, term, examNumber) => {
    setEditingCell({ student_id: row.student_id, lesson_id: row.lesson_id, lesson_code: row.lesson_code, lesson_name: row.lesson_name, term, exam_number: examNumber });
    const currentVal = term === 1 ? row.term1[examNumber] : row.term2[examNumber];
    setPendingValue(currentVal == null ? '' : String(currentVal));
  };
  const cancelEdit = () => { setEditingCell(null); setPendingValue(''); };

  // (Deprecated placeholder removed: previous saveEdit with invalid syntax)
  // Correct saveEdit implementation (redefine to avoid patch complexity above)
  const saveEditFinal = async () => {
    if (!editingCell) return;
    const val = pendingValue.trim();
    const numeric = val === '' ? null : Number(val.replace(',', '.'));
    if (numeric != null && (!Number.isFinite(numeric) || numeric < 0 || numeric > 100)) {
      onError && onError('Geçersiz puan (0-100)');
      return;
    }
    setSaving(true);
    try {
      if (numeric == null) {
        // Clear score: delete existing record if present
        const delBody = {
          student_id: editingCell.student_id,
          lesson_id: editingCell.lesson_id,
          term: editingCell.term,
          exam_number: editingCell.exam_number,
        };
        const res = await fetchJson(apis.delete(schoolId), { method: 'DELETE', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(delBody) });
        if (!res.ok) throw new Error('fail');
      } else {
        const body = {
          student_id: editingCell.student_id,
          lesson_id: editingCell.lesson_id,
          term: editingCell.term,
          exam_number: editingCell.exam_number,
          score: numeric,
          exam_date: null,
        };
        const res = await fetchJson(apis.upsert(schoolId), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
        if (!res.ok) throw new Error('fail');
      }
      await loadResults();
      cancelEdit();
    } catch (e) {
      onError && onError('Kaydedilemedi');
    } finally { setSaving(false); }
  };

  const columns = [1,2,3];

  const EmptyState = ({ message }) => (
    <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
      <Typography variant="body2">{message}</Typography>
    </Box>
  );

  // Simplify complex nested ternaries: produce main content via imperative conditions
  const renderResultsContent = () => {
    if (!selectedClass) return <EmptyState message="Önce bir şube seçin" />;
    if (loadingStudents || loadingResults) {
      return (
        <Box sx={{ p:2 }}>
          {[...Array(6)].map((_,i)=>(<Skeleton key={i} variant="rectangular" height={44} sx={{ mb:1.25, borderRadius:1 }} />))}
        </Box>
      );
    }
    if (visibleRows.length === 0) return <EmptyState message="Henüz sonuç yok" />;
    if (isMobile) {
      return (
        <Box sx={{ maxHeight: 520, overflowY: 'auto', p:1 }}>
          {visibleRows.map((row, idx) => (
            <Box key={`${row.student_id}-${row.lesson_id}`} sx={{ mb:1.5, p:1.25, border:'1px solid', borderColor:'divider', borderRadius:1.5, bgcolor: idx % 2 === 0 ? 'background.default' : 'background.paper' }}>
              <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', mb:1 }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight:600 }}>{row.full_name}</Typography>
                  <Typography variant="caption" color="text.secondary">#{row.student_id}</Typography>
                </Box>
                <Box sx={{ textAlign:'right' }}>
                  <Typography variant="body2" sx={{ fontWeight:600 }}>{row.lesson_name}</Typography>
                  <Typography variant="caption" color="text.secondary">{row.lesson_code}</Typography>
                </Box>
              </Box>
              <Box sx={{ mb:1 }}>
                <Typography variant="caption" sx={{ fontWeight:600, display:'block', mb:0.5 }}>1. Dönem</Typography>
                <Box sx={{ display:'flex', flexWrap:'wrap', gap:0.75 }}>
                  {columns.map(c => {
                    const val = row.term1[c];
                    const editing = editingCell && editingCell.student_id===row.student_id && editingCell.lesson_id===row.lesson_id && editingCell.term===1 && editingCell.exam_number===c;
                    return editing ? (
                      <Box key={`m1-${c}`} sx={{ display:'flex', alignItems:'center', gap:0.5 }}>
                        <TextField size="small" value={pendingValue} onChange={e=> setPendingValue(e.target.value)} onKeyDown={e=> { if (e.key==='Enter') saveEditFinal(); if (e.key==='Escape') cancelEdit(); }} autoFocus sx={{ width:72 }} />
                        <IconButton size="small" aria-label="Kaydet" onClick={saveEditFinal} disabled={saving}><CheckCircleIcon fontSize="inherit" /></IconButton>
                        <IconButton size="small" aria-label="İptal" onClick={cancelEdit}><CloseIcon fontSize="inherit" /></IconButton>
                      </Box>
                    ) : (
                      <Chip key={`m1-${c}`} label={`${c}. Sınav: ${val == null ? '-' : val}`} onClick={()=> startEdit(row,1,c)} size="small" variant="outlined" />
                    );
                  })}
                  <Chip label={`Ort: ${row.term1.avg == null ? '-' : row.term1.avg.toFixed(2)}`} size="small" color="primary" />
                </Box>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ fontWeight:600, display:'block', mb:0.5 }}>2. Dönem</Typography>
                <Box sx={{ display:'flex', flexWrap:'wrap', gap:0.75 }}>
                  {columns.map(c => {
                    const val = row.term2[c];
                    const editing = editingCell && editingCell.student_id===row.student_id && editingCell.lesson_id===row.lesson_id && editingCell.term===2 && editingCell.exam_number===c;
                    return editing ? (
                      <Box key={`m2-${c}`} sx={{ display:'flex', alignItems:'center', gap:0.5 }}>
                        <TextField size="small" value={pendingValue} onChange={e=> setPendingValue(e.target.value)} onKeyDown={e=> { if (e.key==='Enter') saveEditFinal(); if (e.key==='Escape') cancelEdit(); }} autoFocus sx={{ width:72 }} />
                        <IconButton size="small" aria-label="Kaydet" onClick={saveEditFinal} disabled={saving}><CheckCircleIcon fontSize="inherit" /></IconButton>
                        <IconButton size="small" aria-label="İptal" onClick={cancelEdit}><CloseIcon fontSize="inherit" /></IconButton>
                      </Box>
                    ) : (
                      <Chip key={`m2-${c}`} label={`${c}. Sınav: ${val == null ? '-' : val}`} onClick={()=> startEdit(row,2,c)} size="small" variant="outlined" />
                    );
                  })}
                  <Chip label={`Ort: ${row.term2.avg == null ? '-' : row.term2.avg.toFixed(2)}`} size="small" color="success" />
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      );
    }
    // Desktop table
    return (
      <Table size="small" sx={{ '& th': { whiteSpace:'nowrap' } }}>
        <TableHead>
          <TableRow>
            <TableCell>Öğrenci</TableCell>
            <TableCell>Ders</TableCell>
            <TableCell align="center" colSpan={4} sx={{ backgroundColor: termColors.t1Header, borderLeft: `1px solid ${termColors.divider}` }}>1. Dönem</TableCell>
            <TableCell align="center" colSpan={4} sx={{ backgroundColor: termColors.t2Header, borderLeft: `2px solid ${termColors.divider}` }}>2. Dönem</TableCell>
          </TableRow>
          <TableRow>
            <TableCell sx={{ fontSize:12, color:'text.secondary' }}>Ad Soyad</TableCell>
            <TableCell sx={{ fontSize:12, color:'text.secondary' }}>Ders</TableCell>
            {columns.map(c => <TableCell key={`t1-${c}`} align="right" sx={{ backgroundColor: termColors.t1Header }}>{c}. Sınav</TableCell>)}
            <TableCell align="right" sx={{ backgroundColor: termColors.t1Header, fontWeight:600 }}>Ort.</TableCell>
            {columns.map(c => <TableCell key={`t2-${c}`} align="right" sx={{ backgroundColor: termColors.t2Header }}>{c}. Sınav</TableCell>)}
            <TableCell align="right" sx={{ backgroundColor: termColors.t2Header, fontWeight:600 }}>Ort.</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {visibleRows.map(row => (
            <TableRow key={`${row.student_id}-${row.lesson_id}`} hover>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight:500 }}>{row.full_name}</Typography>
                <Typography variant="caption" color="text.secondary">#{row.student_id}</Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight:500 }}>{row.lesson_name}</Typography>
                <Typography variant="caption" color="text.secondary">{row.lesson_code}</Typography>
              </TableCell>
              {columns.map(c => {
                const val = row.term1[c];
                const editing = editingCell && editingCell.student_id===row.student_id && editingCell.lesson_id===row.lesson_id && editingCell.term===1 && editingCell.exam_number===c;
                return (
                  <TableCell key={`r1-${c}`} align="right" sx={{ minWidth:70, backgroundColor: termColors.t1Cell }}>
                    {editing ? (
                      <Box sx={{ display:'flex', alignItems:'center', justifyContent:'flex-end', gap:0.5 }}>
                        <TextField size="small" value={pendingValue} onChange={e=> setPendingValue(e.target.value)} onKeyDown={e=> { if (e.key==='Enter') saveEditFinal(); if (e.key==='Escape') cancelEdit(); }} autoFocus sx={{ width:70 }} />
                        <IconButton size="small" aria-label="Kaydet" onClick={saveEditFinal} disabled={saving}><CheckCircleIcon fontSize="inherit" /></IconButton>
                        <IconButton size="small" aria-label="İptal" onClick={cancelEdit}><CloseIcon fontSize="inherit" /></IconButton>
                      </Box>
                    ) : (
                      <Box sx={{ display:'flex', alignItems:'center', justifyContent:'flex-end' }}>
                        <Typography variant="body2" sx={{ fontWeight:500 }}>{val == null ? '-' : val}</Typography>
                        <Tooltip title="Düzenle"><IconButton size="small" aria-label="Düzenle" onClick={()=> startEdit(row,1,c)}><EditIcon fontSize="inherit" /></IconButton></Tooltip>
                      </Box>
                    )}
                  </TableCell>
                );
              })}
              <TableCell align="right" sx={{ fontWeight:600, backgroundColor: termColors.t1Cell }}>{row.term1.avg == null ? '-' : row.term1.avg.toFixed(2)}</TableCell>
              {columns.map(c => {
                const val = row.term2[c];
                const editing = editingCell && editingCell.student_id===row.student_id && editingCell.lesson_id===row.lesson_id && editingCell.term===2 && editingCell.exam_number===c;
                return (
                  <TableCell key={`r2-${c}`} align="right" sx={{ minWidth:70, backgroundColor: termColors.t2Cell }}>
                    {editing ? (
                      <Box sx={{ display:'flex', alignItems:'center', justifyContent:'flex-end', gap:0.5 }}>
                        <TextField size="small" value={pendingValue} onChange={e=> setPendingValue(e.target.value)} onKeyDown={e=> { if (e.key==='Enter') saveEditFinal(); if (e.key==='Escape') cancelEdit(); }} autoFocus sx={{ width:70 }} />
                        <IconButton size="small" aria-label="Kaydet" onClick={saveEditFinal} disabled={saving}><CheckCircleIcon fontSize="inherit" /></IconButton>
                        <IconButton size="small" aria-label="İptal" onClick={cancelEdit}><CloseIcon fontSize="inherit" /></IconButton>
                      </Box>
                    ) : (
                      <Box sx={{ display:'flex', alignItems:'center', justifyContent:'flex-end' }}>
                        <Typography variant="body2" sx={{ fontWeight:500 }}>{val == null ? '-' : val}</Typography>
                        <Tooltip title="Düzenle"><IconButton size="small" aria-label="Düzenle" onClick={()=> startEdit(row,2,c)}><EditIcon fontSize="inherit" /></IconButton></Tooltip>
                      </Box>
                    )}
                  </TableCell>
                );
              })}
              <TableCell align="right" sx={{ fontWeight:600, backgroundColor: termColors.t2Cell }}>{row.term2.avg == null ? '-' : row.term2.avg.toFixed(2)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  return (
    <Box>
      {/* Top bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Okul Sınav Sonuçları</Typography>
          <Typography variant="caption" color="text.secondary">Şube ve derse göre düzenleyin; dönem ortalamaları</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Yenile">
            <span>
              <IconButton onClick={() => loadResults()} disabled={loadingResults || !selectedClass}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* Filters */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Autocomplete
          size="small"
          sx={{ minWidth: 240 }}
          options={classes}
          getOptionLabel={(opt)=> opt.name || ''}
          value={selectedClass || null}
          onChange={(_,v)=> { setSelectedClass(v || null); setSelectedLesson(null); setSelectedStudentId(''); }}
          loading={loadingClasses}
          renderInput={(params) => (
            <TextField {...params} label="Şube" placeholder="Seç / yaz" InputProps={{ ...params.InputProps, endAdornment: (<>
              {loadingClasses ? <Skeleton variant="circular" width={16} height={16} /> : null}
              {params.InputProps.endAdornment}
            </>) }} />
          )}
          clearOnEscape
        />
        <Autocomplete
          size="small"
          sx={{ minWidth: 260 }}
          options={students.map(s => ({ id: s.id, label: `${(s.name||'')} ${(s.surname||'')}`.trim() + ` (#${s.id})` }))}
          getOptionLabel={(opt)=> opt.label || ''}
          value={selectedStudentId ? (students.find(s=> s.id===selectedStudentId) ? { id: selectedStudentId, label: `${(students.find(s=> s.id===selectedStudentId)?.name||'')} ${(students.find(s=> s.id===selectedStudentId)?.surname||'')}`.trim() + ` (#${selectedStudentId})` } : null) : null}
          onChange={(_,v)=> { setSelectedStudentId(v?.id || ''); }}
          loading={loadingStudents}
          disabled={!selectedClass || loadingStudents}
          renderInput={(params) => (
            <TextField {...params} label="Öğrenci" placeholder="Seç / yaz" InputProps={{ ...params.InputProps, endAdornment: (<>
              {loadingStudents ? <Skeleton variant="circular" width={16} height={16} /> : null}
              {params.InputProps.endAdornment}
            </>) }} />
          )}
          clearOnEscape
        />
        <Autocomplete
          size="small"
          sx={{ minWidth: 240 }}
          options={lessonOptions}
          getOptionLabel={(opt)=> opt.label || ''}
          value={selectedLesson ? lessonOptions.find(l=> l.lesson_id===selectedLesson.lesson_id) || null : null}
          onChange={(_,v)=> { setSelectedLesson(v || null); }}
          loading={loadingLessons}
          renderInput={(params) => (
            <TextField {...params} label="Ders" placeholder="Seç / yaz" InputProps={{ ...params.InputProps, endAdornment: (<>
              {loadingLessons ? <Skeleton variant="circular" width={16} height={16} /> : null}
              {params.InputProps.endAdornment}
            </>) }} />
          )}
          disabled={!selectedClass || loadingStudents}
          clearOnEscape
        />
      </Box>

      <Paper sx={{ p:0, borderRadius:2 }}>
        {renderResultsContent()}
      </Paper>
      <Divider sx={{ mt:3, mb:1 }} />
      <Typography variant="caption" color="text.secondary">Şube seçtikten sonra isteğe bağlı olarak öğrenci veya ders filtreleyebilirsiniz. Ders seçmezseniz tüm dersler listelenir. Boş hücre sınav yapılmadı anlamına gelir ve kayıt oluşturulmaz; ortalama sadece girilen puanlar üzerinden hesaplanır.</Typography>
    </Box>
  );
}
